import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Record } from 'immutable';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Constants
import RecurringWorkspaceItemConstants from 'Shared/constants/RecurringWorkspaceItem.constants';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';
import RecurringWorkspaceItemHelper from 'Shared/model_helpers/RecurringWorkspaceItem.helper';
import { getRecurrenceAttributesFromOptionValue, getSelectedOptionValue, NEW_TIME_OF_DAY_DEFAULT } from '../utils';

// Actions
import * as MeetingItemActions from 'Shared/actions/MeetingItems.actions';

// Components
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { WpModal, WpModalBody, WpModalHeader, WpModalFooter } from 'Wpds/components/modal';
import RevisitEditor from './RevisitEditor';
import WpSelectV2 from 'Wpds/components/select/WpSelectV2';
import WpCheckbox from 'Wpds/components/checkbox/WpCheckbox';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';

// Styles
import './RevisitModal.scss';

type Props = {
  isOpen: boolean;
  toggleModal(): void;
  meetingItem: Record<MeetingItem>;
};

const RevisitModal = ({ isOpen, toggleModal, meetingItem }: Props) => {
  const dispatch = useDispatch();
  const { tr } = useTranslation();

  const uuid = MeetingItemHelpers.getUuid(meetingItem);
  const recurringWorkspaceItem = MeetingItemHelpers.getRcurringWorkspaceItem(meetingItem);
  const parentIsSection = MeetingItemHelpers.getParentIsSection(meetingItem);

  const timeOfDay = RecurringWorkspaceItemHelper.getTimeOfDay(recurringWorkspaceItem);
  const dayOfWeek = RecurringWorkspaceItemHelper.getDayOfWeek(recurringWorkspaceItem);
  const intervalUnit = RecurringWorkspaceItemHelper.getIntervalUnit(recurringWorkspaceItem);
  const intervalAmount = RecurringWorkspaceItemHelper.getIntervalAmount(recurringWorkspaceItem);
  const startsAt = RecurringWorkspaceItemHelper.getStartsAt(recurringWorkspaceItem);
  const dayInMonth = RecurringWorkspaceItemHelper.getDayInMonth(recurringWorkspaceItem);
  const weekInMonth = RecurringWorkspaceItemHelper.getWeekInMonth(recurringWorkspaceItem);
  const onlyRecurOnce = RecurringWorkspaceItemHelper.getOnlyRecurOnce(recurringWorkspaceItem);
  const sectionRuleId = RecurringWorkspaceItemHelper.getSectionRuleId(recurringWorkspaceItem);

  const applyToTopicsInSectionInitialValue = sectionRuleId && parentIsSection;
  const [applyToTopicsInSection, setApplyToTopicsInSection] = useState(applyToTopicsInSectionInitialValue);
  const [showConfirmOverrideModal, toggleShowConfirmOverrideModal] = useToggle(false);

  const [isDeleteing, toggleIsDeleting] = useToggle(false);
  const [isSaving, toggleIsSaving] = useToggle(false);
  const [hasError, setHasError] = useState(false);

  const initialDayValue = dayOfWeek || 'Monday';

  const [tempIntervalAmount, setTempIntervalAmount] = useState(intervalAmount || 1);
  const [tempIntervalUnit, setTempIntervalUnit] = useState(intervalUnit || 'week');
  const [tempDayOfWeek, setTempDayOfWeek] = useState(initialDayValue);
  const [tempTimeOfDay, setTempTimeOfDay] = useState(timeOfDay || NEW_TIME_OF_DAY_DEFAULT);
  const [tempStartsAt, setTempStartsAt] = useState(
    startsAt ? moment(startsAt) : moment().startOf('isoWeek').add(1, 'week')
  );
  const [tempDayInMonth, setTempDayInMonth] = useState(dayInMonth);
  const [tempWeekInMonth, setTempWeekInMonth] = useState(weekInMonth);

  const setTempStartsAtAndPossiblyDay = value => {
    setTempStartsAt(value);
    // if they had a single day selected then set it to the same weekday as the startsAt value
    if (tempIntervalUnit === 'week') {
      const dayOfWeekForValue = moment(value).format('dddd');
      if (!tempDayOfWeek.includes(dayOfWeekForValue)) {
        if (tempDayOfWeek.split(' ').length > 1) setTempDayOfWeek(tempDayOfWeek + ' ' + dayOfWeekForValue);
        else setTempDayOfWeek(dayOfWeekForValue);
      }
    }
  };

  // If any of these changes, then the form becomes dirty and we need to uncheck the applyToTopicsInSection box if it is checked
  const uncheckApplyToTopicsInSectionRef = useRef('not_rendered');
  useEffect(() => {
    if (uncheckApplyToTopicsInSectionRef.current === 'rendered') {
      uncheckApplyToTopicsInSectionRef.current = 'changed_after_rendered';
      if (applyToTopicsInSectionInitialValue && applyToTopicsInSection) setApplyToTopicsInSection(false);
    }
    if (uncheckApplyToTopicsInSectionRef.current === 'not_rendered')
      uncheckApplyToTopicsInSectionRef.current = 'rendered';
  }, [
    tempDayOfWeek,
    tempIntervalAmount,
    tempIntervalUnit,
    tempTimeOfDay,
    tempStartsAt,
    tempDayInMonth,
    tempWeekInMonth,
  ]);

  const handleSaveRecurrenceClick = useCallback(() => {
    toggleIsSaving();
    const data = {
      [RecurringWorkspaceItemConstants.Model.DAY_OF_WEEK]: tempDayOfWeek,
      [RecurringWorkspaceItemConstants.Model.INTERVAL_AMOUNT]: tempIntervalAmount,
      [RecurringWorkspaceItemConstants.Model.INTERVAL_UNIT]: tempIntervalUnit,
      [RecurringWorkspaceItemConstants.Model.TIME_OF_DAY]: tempTimeOfDay,
      [RecurringWorkspaceItemConstants.Model.STARTS_AT]: moment(tempStartsAt).format(),
      [RecurringWorkspaceItemConstants.Model.DAY_IN_MONTH]: tempDayInMonth,
      [RecurringWorkspaceItemConstants.Model.WEEK_IN_MONTH]: tempWeekInMonth,
      section_rule_action: applyToTopicsInSection
        ? 'upsert'
        : applyToTopicsInSectionInitialValue && uncheckApplyToTopicsInSectionRef.current !== 'changed_after_rendered'
        ? 'destroy'
        : 'disconnect',
      confirm: showConfirmOverrideModal,
    };
    dispatch(
      MeetingItemActions.updateRecurringItem(
        uuid,
        data,
        !recurringWorkspaceItem ? 'Recurrence created' : 'Recurrence updated'
      )
    )
      .then(() => {
        toggleModal();
      })
      .catch(e => {
        if (e?.response?.data?.error === 'confirmation_needed') {
          toggleShowConfirmOverrideModal();
        }
      })
      .finally(() => toggleIsSaving());
  }, [
    toggleIsSaving,
    tempDayOfWeek,
    tempIntervalAmount,
    tempIntervalUnit,
    tempTimeOfDay,
    tempStartsAt,
    tempDayInMonth,
    tempWeekInMonth,
    applyToTopicsInSection,
    showConfirmOverrideModal,
  ]);

  const handleRemoveClick = useCallback(() => {
    toggleIsDeleting();
    dispatch(MeetingItemActions.deleteRecurrenceItem(uuid)).then(() => {
      toggleIsDeleting();
      toggleModal();
    });
  }, [toggleIsDeleting]);

  const handleSetIntervalAmount = amount => {
    if (!amount) {
      setHasError(true);
    } else {
      setHasError(false);
    }
    setTempIntervalAmount(amount);
  };

  const showDeleteButton = recurringWorkspaceItem;
  const buttonsDisabled = isDeleteing || isSaving;

  const standardOrCustomDropdownOptions = [
    { label: tr('perform.meeting_item.revisit_dropdown.daily_monday_friday'), value: 'daily' },
    { label: tr('perform.meeting_item.revisit_dropdown.weekly_on_monday'), value: 'weekly_on_monday' },
    { label: tr('perform.meeting_item.revisit_dropdown.monthly_first_monday'), value: 'monthly_on_first_monday' },
    { label: tr('perform.meeting_item.revisit_modal.custom'), value: 'custom' },
  ];

  const [standardOrCustomDropdownValue, setStandardOrCustomDropdownValue] = useState(
    getSelectedOptionValue({
      timeOfDay,
      dayOfWeek,
      intervalUnit,
      intervalAmount,
      weekInMonth,
      onlyRecurOnce,
      recurringWorkspaceItemExists: !!recurringWorkspaceItem,
    }) || 'custom'
  );

  const handleStandardOrCustomDropdownSelectionChange = option => {
    setStandardOrCustomDropdownValue(option);
    const recurrenceAttributes = getRecurrenceAttributesFromOptionValue(option);
    if (recurrenceAttributes) {
      setTempDayOfWeek(recurrenceAttributes.dayOfWeek);
      setTempTimeOfDay(recurrenceAttributes.timeOfDay);
      setTempIntervalUnit(recurrenceAttributes.intervalUnit);
      setTempIntervalAmount(recurrenceAttributes.intervalAmount);
      setTempWeekInMonth(recurrenceAttributes.weekInMonth);
      setTempDayInMonth(recurrenceAttributes.dayInMonth);
      setTempStartsAt(moment(recurrenceAttributes.startsAt));
    }
  };

  if (!isOpen) return null;

  if (showConfirmOverrideModal)
    return (
      <ConfirmModal
        message={tr('perform.meeting_item.revisit_dropdown.override_confirm_message')}
        isOpen
        onToggle={toggleShowConfirmOverrideModal}
        onConfirmClick={() => handleSaveRecurrenceClick()}
        confirmButtonLabel={tr('perform.meeting_item.revisit_dropdown.override_confirm_button')}
      />
    );

  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={toggleModal}>
      <WpModalHeader>{tr('perform.meeting_item.revisit_modal.title')}</WpModalHeader>
      <WpModalBody>
        {showConfirmOverrideModal && (
          <ConfirmModal
            message={tr('perform.meeting_item.revisit_modal.confirm_delete_account.message')}
            isOpen
            onToggle={toggleShowConfirmOverrideModal}
            onConfirmClick={() => console.log('bad')}
            confirmButtonLabel={tr('perform.meeting_item.revisit_modal.confirm_delete_account.delete')}
          />
        )}
        {tr('perform.meeting_item.revisit_modal.description')}
        <div className="mb-3">
          <WpSelectV2
            id="standard-or-custom-dropdown"
            placeholder={tr('perform.meeting_item.revisit_dropdown.set_schedule')}
            label={tr('perform.meeting_item.revisit_modal.revisit_label')}
            value={standardOrCustomDropdownValue}
            options={standardOrCustomDropdownOptions}
            onChange={handleStandardOrCustomDropdownSelectionChange}
          />
        </div>
        {standardOrCustomDropdownValue === 'custom' && (
          <div className="RevisitModal__editor">
            <form>
              <RevisitEditor
                days={tempDayOfWeek}
                setDay={setTempDayOfWeek}
                timeOfDay={tempTimeOfDay}
                setTimeOfDay={setTempTimeOfDay}
                intervalUnit={tempIntervalUnit}
                setIntervalUnit={setTempIntervalUnit}
                intervalAmount={tempIntervalAmount}
                setIntervalAmount={handleSetIntervalAmount}
                startsAt={tempStartsAt}
                setStartsAt={setTempStartsAtAndPossiblyDay}
                dayInMonth={tempDayInMonth}
                setDayInMonth={setTempDayInMonth}
                weekInMonth={tempWeekInMonth}
                setWeekInMonth={setTempWeekInMonth}
                hideTimeOfDayInput={timeOfDay === NEW_TIME_OF_DAY_DEFAULT || !timeOfDay}
                hideRecurrenceLabel
              />
            </form>
          </div>
        )}
        {parentIsSection && (
          <div>
            <WpCheckbox
              id="apply-to-topics-in-section"
              label={tr('perform.meeting_item.revisit_modal.apply_to_all_topics')}
              checked={applyToTopicsInSection}
              onChange={setApplyToTopicsInSection}
            />
          </div>
        )}
      </WpModalBody>
      <WpModalFooter>
        <div className="RevisitModal__footer">
          {showDeleteButton && (
            <WpButton variant="tertiary" onClick={handleRemoveClick} loading={isDeleteing} disabled={buttonsDisabled}>
              {tr('perform.meeting_item.revisit_dropdown.stop_revisiting')}
            </WpButton>
          )}
          <div className="ml-auto">
            <WpButtonGroup>
              <WpButton variant="tertiary" onClick={() => toggleModal()} disabled={buttonsDisabled}>
                {tr('perform.meeting_item.revisit_modal.cancel')}
              </WpButton>
              <WpButton
                onClick={handleSaveRecurrenceClick}
                disabled={
                  buttonsDisabled ||
                  hasError ||
                  !standardOrCustomDropdownValue ||
                  standardOrCustomDropdownValue === 'existing_snooze'
                }
                loading={isSaving}
              >
                {tr('perform.meeting_item.revisit_modal.save')}
              </WpButton>
            </WpButtonGroup>
          </div>
        </div>
      </WpModalFooter>
    </WpModal>
  );
};

export default RevisitModal;
